import type { ReactNode } from "react"
import { ModuleStyle } from "@/modules/module-style"

/**
 * THE ONE CLOSING-BAND SHELL.
 *
 * His law: "all the CTA modules in all the pages should just be the exact
 * same format They should just different cells in the same CTA Module", and
 * "The closing band aka the CTA band should contain all the CTE cells from
 * every single page And they should have the exact same UI design with just a
 * copy looking different".
 *
 * So the band's markup and numbers live here ONCE. Every closing-band cell
 * renders this component and differs only in its words. The optional slots
 * below are cell differences, never a second component: a cell that has a
 * second line renders one, a cell that has none renders nothing.
 *
 * It sits in its own file, apart from the instances, for one reason: a cell
 * whose page needs client state (the post-booking close reads the booking out
 * of the URL) imports the shell directly, and importing the instances file
 * would drag the billing config into the client bundle with it.
 *
 * A page built in the older `.dark`-scoped idiom styles its own band from its
 * own stylesheet; this shell is for the cells whose host page carries no such
 * stylesheet, and it reproduces the approved band's numbers in a scoped style
 * block so the two renders are identical.
 *
 * TWO WEIGHTS. Every article and every situation page closes on this shell,
 * so it follows the reading surface's weight law (his ruling on the one-page
 * structure): the paragraph and the fine line at 400, the heading, the second
 * line and the pill at the one bold weight, 800. Nothing at 500, 600 or 700.
 * The courses close in instances.tsx carries the same numbers.
 */
export const STANDALONE_CLOSING_BAND_CSS = `
.cbstd{position:relative}
.cbstd .dark{background:var(--color-ink-band);color:#fff;text-align:center;padding:68px 26px;
  font-family:var(--font-jakarta),Inter,ui-sans-serif,system-ui,sans-serif}
.cbstd .dark h2{font-size:clamp(1.7rem,3.2vw,2.5rem);letter-spacing:-.04em;font-weight:800;margin:0 0 10px}
.cbstd .dark p{color:#9ca3af;font-size:15px;font-weight:400;margin:0 0 24px;max-width:640px;
  margin-left:auto;margin-right:auto;line-height:1.6}
.cbstd .dark p.csub{color:#fff;font-size:clamp(1.05rem,1.9vw,1.3rem);font-weight:800;
  letter-spacing:-.03em;line-height:1.2;max-width:34ch;margin:0 auto 14px}
.cbstd .dark .cta{display:inline-flex;align-items:center;gap:9px;background:var(--color-vermilion);
  color:#fff;text-decoration:none;border-radius:999px;padding:15px 32px;font-size:15px;font-weight:800;
  margin-top:0}
.cbstd .dark .fine2{color:#9ca3af;font-size:13px;line-height:1.8;margin:24px auto 0}
`

export function StandaloneClosingBand({
  heading,
  subhead,
  body,
  ctaLabel,
  ctaHref,
  ctaData,
  action,
  fine2,
  extra,
}: {
  heading: string
  /** The second, bolder line some cells carry under the heading. */
  subhead?: string
  body: string
  /** The pill. A cell that closes on a plain link gives both halves. */
  ctaLabel?: string
  ctaHref?: string
  /** Plain data attributes the host page's own click listener reads off the
   *  button. The situation pages measure their final CTA this way, so the
   *  attributes travel with the cell rather than with the page markup. */
  ctaData?: Record<string, string>
  /** The action for a cell whose close is not a plain link, such as the
   *  post-booking add-to-calendar control. It takes the pill's place. */
  action?: ReactNode
  fine2?: string
  /** A richer closing line under fine2 (e.g. one carrying its own link),
   *  for the one cell that needs more than fine2's plain string - kept
   *  inside the same `.dark` section so the band stays one continuous box
   *  rather than two stacked ones. */
  extra?: ReactNode
}) {
  return (
    <div className="cbstd">
      <ModuleStyle css={STANDALONE_CLOSING_BAND_CSS} />
      <section className="dark">
        <h2>{heading}</h2>
        {subhead ? <p className="csub">{subhead}</p> : null}
        <p>{body}</p>
        {ctaLabel && ctaHref ? (
          <a className="cta" href={ctaHref} {...ctaData}>
            {ctaLabel} <span aria-hidden>&rsaquo;</span>
          </a>
        ) : null}
        {action}
        {fine2 ? <p className="fine2">{fine2}</p> : null}
        {extra}
      </section>
    </div>
  )
}
